/**
 * List rule - logical expression for matching leads lists by tags
 * For Media Devoted | Created By Ciro Autiero
 */

export type ListRuleTag = { type: "tag"; tag: string };
export type ListRuleAnd = { type: "and"; left: ListRuleNode; right: ListRuleNode };
export type ListRuleOr = { type: "or"; left: ListRuleNode; right: ListRuleNode };

export type ListRuleNode = ListRuleTag | ListRuleAnd | ListRuleOr;

export function evaluateListRule(node: ListRuleNode, listTags: string[]): boolean {
  const tags = listTags.map((t) => t.trim().toLowerCase());
  return evalNode(node, tags);
}

function evalNode(node: ListRuleNode, tags: string[]): boolean {
  switch (node.type) {
    case "tag":
      return tags.includes(node.tag.trim().toLowerCase());
    case "and":
      return evalNode(node.left, tags) && evalNode(node.right, tags);
    case "or":
      return evalNode(node.left, tags) || evalNode(node.right, tags);
    default:
      return false;
  }
}

export function listRuleFromGroups(groups: string[][]): ListRuleNode | null {
  if (groups.length === 0) return null;
  const orNodes = groups.map((tags) => orGroup(tags));
  return orNodes.reduce((acc, n) => (acc ? { type: "and", left: acc, right: n } : n));
}

function orGroup(tags: string[]): ListRuleNode {
  const trimmed = tags.filter((t) => t.trim().length > 0).map((t) => t.trim());
  if (trimmed.length === 0) return { type: "tag", tag: "" };
  if (trimmed.length === 1) return { type: "tag", tag: trimmed[0] };
  return trimmed.slice(1).reduce(
    (acc, tag) => ({ type: "or", left: acc, right: { type: "tag", tag } }),
    { type: "tag", tag: trimmed[0] } as ListRuleNode,
  );
}

/** Human-readable rule string, e.g. "(New OR Elite Profit) AND (High Profit)" */
export function formatListRule(node: ListRuleNode | Record<string, unknown> | null | undefined): string {
  const n = node as ListRuleNode | null | undefined;
  if (!n || typeof n !== "object") return "";
  if (n.type === "tag") return (n.tag as string)?.trim() || "";
  if (n.type === "or") {
    const left = formatListRule(n.left as ListRuleNode);
    const right = formatListRule(n.right as ListRuleNode);
    if (!left && !right) return "";
    if (!left) return right;
    if (!right) return left;
    return `(${left} OR ${right})`;
  }
  if (n.type === "and") {
    const left = formatListRule(n.left as ListRuleNode);
    const right = formatListRule(n.right as ListRuleNode);
    if (!left && !right) return "";
    if (!left) return right;
    if (!right) return left;
    return `(${left} AND ${right})`;
  }
  return "";
}

/** Parse ListRuleNode AST back to groups (for editing) */
export function listRuleToGroups(node: ListRuleNode | Record<string, unknown> | null | undefined): string[][] {
  const n = node as ListRuleNode | null | undefined;
  if (!n || typeof n !== "object") return [];
  if (n.type === "tag") {
    const t = (n.tag as string)?.trim();
    return t ? [[t]] : [];
  }
  if (n.type === "or") {
    const left = listRuleToGroups(n.left as ListRuleNode);
    const right = listRuleToGroups(n.right as ListRuleNode);
    if (left.length === 0) return right;
    if (right.length === 0) return left;
    const last = left[left.length - 1] ?? [];
    const rightTags = right.flat();
    return [...left.slice(0, -1), [...last, ...rightTags]];
  }
  if (n.type === "and") {
    const left = listRuleToGroups(n.left as ListRuleNode);
    const right = listRuleToGroups(n.right as ListRuleNode);
    return [...left, ...right].filter((g) => g.length > 0);
  }
  return [];
}
